import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { translateSupabaseError } from '@/utils/errorTranslation';
import { usePurchaseInvoices } from '@/hooks/usePurchaseInvoices';
import { usePaymentConditions, calculateDueDates } from '@/hooks/usePaymentConditions';
import { addDays, format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { usePurchases } from '@/hooks/usePurchases';
import { useCurrentCompanyId } from '@/hooks/useCurrentCompanyId';
import type { PendingPurchaseInvoice, PurchaseNature } from '@/types/purchaseInvoice';
import { NATURE_MOVES_STOCK } from '@/types/purchaseInvoice';
import type { ImportLinkUnitInfo } from '../ImportItemUnitPicker';

// Steps
import { Step1Company } from './Step1Company';
import { Step2Xml } from './Step2Xml';
import { Step3Nature } from './Step3Nature';
import { Step4Products } from './Step4Products';
import { Step5Confirm } from './Step5Confirm';

interface PurchaseInvoiceWizardProps {
  invoice: PendingPurchaseInvoice;
  open: boolean;
  onClose: () => void;
}

export interface WizardData {
  invoice: PendingPurchaseInvoice;
  xmlContent: string | null;
  parsedXml: any | null;
  nature: PurchaseNature;
  generateFinancial: boolean;
  productMappings: ProductMapping[];
  paymentConditionId?: string;
  /** C-6 (D-C.5): categoria de despesa do cabeçalho da compra, escolhida no passo Concluir. */
  expenseCategoryId?: string;
  /** C-6 (D-C.8): categoria dos itens de uso interno; só preenchida quando há item internal_use. */
  internalUseCategoryId?: string;
  /** R-COMPRAS.18: Centro de Custo do cabeçalho da compra — opcional, escolhido no passo Concluir. */
  costCenterId?: string;
  /**
   * FIN-FORMA-OBRIGATORIA Ato 1 (I.168): código da forma de pagamento, obrigatório no passo Concluir.
   * undefined = ainda não pré-marcada pela tag da nota; '' = a nota não define forma do cadastro.
   */
  paymentMethod?: string;
}

export interface ProductMapping {
  xmlItem: any;
  productId: string | null;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
  cfop: string;
  /** I.151 1b3-b: unidade do produto escolhida (null = base; undefined = não definida → bloqueia).
   *  quantity/unitPrice ficam como na nota (qCom/vUnCom); quem converte é o banco. */
  productUnitId?: string | null;
  /** sigla da unidade escolhida (resumo do passo 5) */
  unitLabel?: string;
  /** vínculo do fornecedor para o cProd (memória de unidade, fator antigo, base confirmada) */
  link?: ImportLinkUnitInfo | null;
  /** A-tric.8: sobrescrita por item; ausente = herda da natureza (effectiveItemType). */
  itemType?: 'resale' | 'internal_use';
}

/**
 * A-tric.8: tipo efetivo do item — sobrescrita explícita ou derivado da natureza
 * (NATURE_MOVES_STOCK). É o item_type enviado ao motor; o motor curado só
 * movimenta estoque quando item_type <> 'internal_use' (product_id fica como rastro).
 */
export function effectiveItemType(m: ProductMapping, nature: PurchaseNature): 'resale' | 'internal_use' {
  return m.itemType ?? (NATURE_MOVES_STOCK[nature] ? 'resale' : 'internal_use');
}

const STEPS = [
  { id: 1, title: 'Dados da Empresa' },
  { id: 2, title: 'Importação XML' },
  { id: 3, title: 'Natureza da Compra' },
  { id: 4, title: 'Produtos NF-e' },
  { id: 5, title: 'Concluir' },
];

/**
 * Build payment_terms JSONB from XML duplicatas or selected payment condition.
 * Priority: XML duplicatas > Payment condition > single installment fallback.
 */
function buildPaymentTerms(wizardData: WizardData, totalValue: number, purchaseDate: string): { installments: number; due_dates: string[]; values?: number[] } | undefined {
  // Scenario A: XML has duplicatas
  const duplicatas = wizardData.parsedXml?.cobr?.dup;
  if (Array.isArray(duplicatas) && duplicatas.length > 0) {
    return {
      installments: duplicatas.length,
      due_dates: duplicatas.map((d: any) => d.dVenc || purchaseDate),
      values: duplicatas.map((d: any) => parseFloat(d.vDup) || (totalValue / duplicatas.length)),
    };
  }

  // Scenario B: No XML duplicatas - build from paymentConditionId
  // payment_terms will be built in handleFinish using the condition + penny-perfect logic
  return undefined; // Handled below in handleFinish
}

export function PurchaseInvoiceWizard({ invoice, open, onClose }: PurchaseInvoiceWizardProps) {
  const { companyId } = useCurrentCompanyId();
  const { conditions: paymentConditions } = usePaymentConditions();
  const { manifestInvoice, updateInvoiceStatus, downloadDfeXml } = usePurchaseInvoices();
  const { createPurchaseAsync } = usePurchases();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [wizardData, setWizardData] = useState<WizardData>({
    invoice,
    xmlContent: null,
    parsedXml: null,
    nature: 'revenda',
    generateFinancial: true,
    productMappings: [],
  });

  // Arco A / C2: o XML completo vive no bucket privado dfe-documents (xml_storage_path,
  // preenchido pelo dfe-sync quando o procNFe chega). Sem path = só resumo → o wizard
  // NÃO manifesta nada sozinho (D-NF11.8, que revoga D-143.11: a ciência é automática no
  // cron da dfe-sync, com o botão da lista como fallback manual) e não espera por polling:
  // o operador reabre após o próximo sync.
  useEffect(() => {
    const loadXml = async () => {
      if (!invoice.xml_storage_path) return;
      setIsLoading(true);
      try {
        const xml = await downloadDfeXml(invoice.xml_storage_path);
        setWizardData(prev => ({ ...prev, xmlContent: xml }));
      } catch (error) {
        console.error('Error reading XML from bucket:', error);
        toast.error('Erro ao ler o XML da nota', {
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadXml();
  }, [invoice.id, invoice.xml_storage_path]);

  // I.151 1b3-b: aceita também a forma funcional (seletores de unidade que se resolvem juntos)
  const updateWizardData = (updates: Partial<WizardData> | ((prev: WizardData) => Partial<WizardData>)) => {
    setWizardData(prev => ({ ...prev, ...(typeof updates === 'function' ? updates(prev) : updates) }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Check if XML has duplicatas
  const xmlHasDuplicatas = Array.isArray(wizardData.parsedXml?.cobr?.dup) && wizardData.parsedXml.cobr.dup.length > 0;

  const handleFinish = async () => {
    // A-tric.10 (N-FNF.1): guarda espelho do F0 (ImportStep3Products.handleComplete) ANTES de
    // qualquer efeito — item MOVIMENTADOR (motor: COALESCE(item_type,'resale') <> 'internal_use')
    // sem produto vinculado bloqueia; uso interno sem produto é legítimo (product_id NULL).
    const movimentadorSemProduto = wizardData.productMappings.filter(
      m => effectiveItemType(m, wizardData.nature) !== 'internal_use' && !m.productId,
    );
    if (movimentadorSemProduto.length > 0) {
      toast.error(`${movimentadorSemProduto.length} item(ns) para revenda sem produto vinculado`, {
        description: 'Vincule um produto ou classifique o item como Uso Interno no passo 4.',
      });
      return;
    }
    // I.151 1b3-b: item que movimenta estoque sem unidade definida não passa (fail-closed)
    const movimentadorSemUnidade = wizardData.productMappings.filter(
      m => effectiveItemType(m, wizardData.nature) !== 'internal_use' && !!m.productId && m.productUnitId === undefined,
    );
    if (movimentadorSemUnidade.length > 0) {
      toast.error(`${movimentadorSemUnidade.length} item(ns) sem unidade definida`, {
        description: 'No passo 4, confirme "É a base mesmo" ou cadastre a unidade do produto.',
      });
      return;
    }
    // FIN-FORMA-OBRIGATORIA Ato 1 (I.168)
    if (!wizardData.paymentMethod) {
      toast.error("Forma de pagamento é obrigatória");
      return;
    }

    setIsProcessing(true);

    try {
      if (!companyId) {
        throw new Error('Empresa não encontrada');
      }

      // 1. Find or create supplier (BUG-C17: use suppliers table, not customers)
      let supplierId: string | null = null;
      
      if (wizardData.invoice.emitente_cnpj) {
        const cleanCnpj = wizardData.invoice.emitente_cnpj.replace(/\D/g, '');
        
        // Use RPC to find supplier by normalized document
        const { data: existingSupplier } = await supabase.rpc(
          'find_supplier_by_normalized_document',
          { p_company_id: companyId, p_document: cleanCnpj }
        );

        if (existingSupplier && existingSupplier.length > 0) {
          supplierId = existingSupplier[0].id;
        } else {
          // R-COMPRAS.20: criação via RPC (SECURITY DEFINER, tenant do caller) — o INSERT direto
          // esbarrava na policy suppliers_manage (settings:manage_suppliers) para o comprador.
          // purchase_invoices só carrega cnpj/razao_social/uf/ie do emitente — sem fone/endereço.
          const { data: newSupplierId, error: supplierError } = await supabase
            .rpc('upsert_supplier_from_invoice', {
              p_document_number: cleanCnpj,
              p_name: wizardData.invoice.emitente_razao_social || 'Fornecedor',
              p_document_type: 'cnpj',
              p_state: wizardData.invoice.emitente_uf || null,
              p_state_registration: wizardData.invoice.emitente_ie || null,
            });

          if (supplierError) throw supplierError;
          supplierId = newSupplierId;
        }
      }

      // 2. Build payment_terms from XML duplicatas or payment condition
      // (BUG-C14: purchase_number generation is now delegated to createPurchaseAsync)
      const totalValue = wizardData.invoice.total_value || 0;
      const emissionDate = wizardData.invoice.emission_date || new Date().toISOString();
      let paymentTerms = buildPaymentTerms(wizardData, totalValue, emissionDate);

      // Scenario B fix: if no XML duplicatas but paymentConditionId selected, build payment_terms
      if (!paymentTerms && wizardData.paymentConditionId && wizardData.generateFinancial) {
        const selectedCondition = paymentConditions.find(c => c.id === wizardData.paymentConditionId);
        if (selectedCondition) {
          const baseDate = new Date(emissionDate);
          const dueDates = calculateDueDates(selectedCondition, baseDate);
          if (dueDates.length > 0) {
            const n = dueDates.length;
            const baseValue = Math.floor((totalValue / n) * 100) / 100;
            const remainder = Math.round((totalValue - baseValue * n) * 100) / 100;
            const values = Array(n).fill(baseValue);
            values[n - 1] = Math.round((values[n - 1] + remainder) * 100) / 100;
            paymentTerms = {
              installments: n,
              due_dates: dueDates.map(d => format(d, 'yyyy-MM-dd')),
              values,
            };
          }
        }
      }

      // 3. Build items array (BUG-C16: batch instead of loop)
      // §I.143-B lote 5: itens enxutos — subtotal/total/quantity_received são
      // do motor (ramo receive do create), nunca do payload
      // A-tric.10 / D-F0.1: TODOS os itens entram no payload (total e parcelas = NF);
      // sem produto vai com product_id NULL — o motor soma no total e não movimenta.
      // I.151 1b3-b: quantidade e preço COMO NA NOTA (qCom/vUnCom) + unidade escolhida; o banco
      // converte para a base e memoriza o vínculo (supplier_link_learn, pelo código do fornecedor).
      const items = wizardData.productMappings
        .map(m => ({
          product_id: m.productId ?? null,
          product_name: m.productName,
          quantity: m.quantity,
          unit_price: m.unitPrice,
          discount_value: 0,
          item_type: effectiveItemType(m, wizardData.nature),
          measurement_unit: m.xmlItem?.uCom || null,
          product_unit_id: m.productId ? (m.productUnitId ?? null) : null,
          supplier_product_code: m.productId ? (m.xmlItem?.cProd || null) : null,
        }));

      if (!supplierId) {
        throw new Error('Não foi possível identificar o fornecedor da nota');
      }

      // 4. Compra atômica: estoque no depósito principal + payables CP-8 pelo
      // motor (receive: true explícito; totais/recebimento server-side; dedup
      // de invoice_key na transação; created_by = auth.uid() no motor)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const purchaseResult = await createPurchaseAsync({
        purchase_type: 'product',
        movement_type: 'purchase',
        supplier_id: supplierId,
        purchase_date: emissionDate,
        invoice_number: wizardData.invoice.invoice_number,
        invoice_series: wizardData.invoice.invoice_series,
        invoice_key: wizardData.invoice.access_key,
        status: 'received',
        receive: true,
        // T2: Step2Xml monta parsed.total.ICMSTot — o caminho antigo (parsedXml.ICMSTot) era undefined → tudo 0
        discount_value: parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vDesc) || 0,
        discount_percentage: 0,
        freight_value: parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vFrete) || 0,
        insurance_value: parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vSeg) || 0,
        // R-COMPRAS.19: mesma aritmética do upload (ImportStep4Confirm) — IPI + ST + DIFAL + FCP-ST
        tax_value: Math.round(
          ((parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vIPI) || 0) +
           (parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vST) || 0) +
           (parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vICMSUFDest) || 0) +
           (parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vFCPST) || 0)) * 100
        ) / 100,
        other_costs: parseFloat(wizardData.parsedXml?.total?.ICMSTot?.vOutro) || 0,
        ...(paymentTerms ? { payment_terms: paymentTerms } : {}),
        // FIN-FORMA-OBRIGATORIA Ato 1 (I.168): código da forma, levado às contas a pagar pelo gerador
        payment_method: wizardData.paymentMethod,
        ...(wizardData.paymentConditionId && uuidRegex.test(wizardData.paymentConditionId)
          ? { payment_condition: wizardData.paymentConditionId } : {}),
        items,
        // C-6 (D-DRE.1): categorias na RAIZ do payload (motor valida tenant+active). Uso interno
        // só vai quando há item internal_use — mesma condição que exibe o campo no passo Concluir.
        // TODO types.ts regen: colunas do C-5 ainda fora do tipo Purchase
        ...({
          expense_category_id: wizardData.expenseCategoryId || null,
          ...(items.some((i) => i.item_type === 'internal_use')
            ? { internal_use_category_id: wizardData.internalUseCategoryId || null }
            : {}),
          // R-COMPRAS.18: cost_center_id na RAIZ (motor propaga a payables/expenses) — idem TODO
          cost_center_id: wizardData.costCenterId || null,
        } as Record<string, unknown>),
      });

      // 5. I.151 1b3-b: o vínculo fornecedor-produto (produto, unidade, tipo de uso, base
      // confirmada) é memorizado pelo banco dentro da compra (supplier_link_learn) — caminho único;
      // o upsert pelo navegador saiu.

      // 5b. Auto-assign product_type_id for industrialization purchases
      if (wizardData.nature === 'industrializacao') {
        const { data: mfgType } = await supabase
          .from('product_types')
          .select('id')
          .eq('company_id', companyId)
          .eq('allows_manufacturing', true)
          .limit(1)
          .maybeSingle();

        let mfgTypeId = mfgType?.id;

        if (!mfgTypeId) {
          const { data: newType } = await supabase
            .from('product_types')
            .insert({
              company_id: companyId,
              name: 'Matéria-Prima',
              internal_code: 'MP',
              allows_manufacturing: true,
              allows_sale: false,
              allows_rental: false,
              requires_serial_tracking: false,
              icon: 'Factory',
            })
            .select('id')
            .single();
          mfgTypeId = newType?.id;
        }

        if (mfgTypeId) {
          const productIds = wizardData.productMappings
            .filter(m => m.productId)
            .map(m => m.productId!);

          if (productIds.length > 0) {
            await supabase
              .from('products')
              .update({ product_type_id: mfgTypeId })
              .in('id', productIds)
              .is('product_type_id', null);
          }
        }
      }

      // 6. Update invoice status — await REAL (mutateAsync); não bloqueante: a compra já existe
      let posCompraOk = true;
      try {
        await updateInvoiceStatus({
          invoiceId: invoice.id,
          status: 'processed',
          linkedPurchaseId: purchaseResult.purchase_id,
        });
      } catch (statusError) {
        posCompraOk = false;
        console.error('Error updating pending status:', statusError);
        toast.warning('Compra criada, mas a nota não foi marcada como processada', {
          description: translateSupabaseError(statusError),
          duration: 8000,
        });
      }

      // 7. Manifest confirmation (210200) — o onError da mutation já toasta o motivo
      // A-untric.2: pendência já confirmada (vínculo manual anterior / reimportação pós A-tric.9)
      // repetiria o evento → 573 na SEFAZ. Pular em silêncio; posCompraOk segue true.
      if (wizardData.invoice.manifestation_status === 'confirmacao') {
        console.log('[PurchaseInvoiceWizard] confirmação já registrada — manifestação pulada', { invoiceId: invoice.id });
      } else {
        try {
          await manifestInvoice({
            invoiceId: invoice.id,
            manifestationType: 'confirmacao',
          });
        } catch (manifestError) {
          posCompraOk = false;
          console.error('Error manifesting confirmation:', manifestError);
          toast.warning('Compra criada, mas a confirmação da operação não foi registrada na SEFAZ', {
            duration: 8000,
          });
        }
      }

      // A-untric.3: texto honesto por efeito — mesmo predicado movimentador da guarda
      const movimentaEstoque = wizardData.productMappings.some(
        m => !!m.productId && effectiveItemType(m, wizardData.nature) !== 'internal_use',
      );
      toast.success(posCompraOk
        ? (movimentaEstoque
            ? 'Nota fiscal importada com sucesso!'
            : 'Compra criada — sem movimentação de estoque')
        : 'Compra criada — verifique os avisos acima');
      onClose();
    } catch (error) {
      console.error('Error processing invoice:', error);
      toast.error('Erro ao processar nota fiscal', {
        description: translateSupabaseError(error),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return true;
      case 2:
        return !!wizardData.xmlContent || !!wizardData.parsedXml;
      case 3: {
        if (!wizardData.nature) return false;
        // Hard constraint: if generating financial and no XML duplicatas, must select payment condition
        if (wizardData.generateFinancial && !xmlHasDuplicatas && !wizardData.paymentConditionId) {
          return false;
        }
        return true;
      }
      case 4:
        // I.151 1b3-b: item que movimenta estoque com unidade indefinida segura o avanço
        return wizardData.productMappings.length > 0 && !wizardData.productMappings.some(
          m => !!m.productId && effectiveItemType(m, wizardData.nature) !== 'internal_use' && m.productUnitId === undefined,
        );
      case 5:
        // FIN-FORMA-OBRIGATORIA Ato 1 (I.168): forma de pagamento obrigatória
        return !!wizardData.paymentMethod;
      default:
        return false;
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!isProcessing) onClose(); }}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => { if (isProcessing) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isProcessing) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Importar Nota Fiscal de Entrada</DialogTitle>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-4">
          <Progress value={progress} className="h-2" />
          
          <div className="flex justify-between">
            {STEPS.map((step) => (
              <div 
                key={step.id}
                className={`flex flex-col items-center ${
                  step.id === currentStep 
                    ? 'text-primary' 
                    : step.id < currentStep 
                      ? 'text-green-600' 
                      : 'text-muted-foreground'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step.id === currentStep 
                    ? 'bg-primary text-primary-foreground' 
                    : step.id < currentStep 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-muted'
                }`}>
                  {step.id < currentStep ? <Check className="h-4 w-4" /> : step.id}
                </div>
                <span className="text-xs mt-1 hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="py-4 min-h-[400px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">Lendo XML da nota...</p>
            </div>
          ) : (
            <>
              {currentStep === 1 && (
                <Step1Company 
                  data={wizardData} 
                  onUpdate={updateWizardData} 
                />
              )}
              {currentStep === 2 && (
                <Step2Xml 
                  data={wizardData} 
                  onUpdate={updateWizardData} 
                />
              )}
              {currentStep === 3 && (
                <Step3Nature 
                  data={wizardData} 
                  onUpdate={updateWizardData} 
                />
              )}
              {currentStep === 4 && (
                <Step4Products 
                  data={wizardData} 
                  onUpdate={updateWizardData} 
                />
              )}
              {currentStep === 5 && (
                <Step5Confirm 
                  data={wizardData} 
                  onUpdate={updateWizardData} 
                />
              )}
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || isProcessing}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>

          {currentStep < STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || isLoading}
            >
              Próximo
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={!canProceed() || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Concluir Importação
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
